import { ArrowLeftIcon, RocketIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { NotificationListener } from 'components/main/listeners/notification';
import { PlatformConsoleNav } from 'components/platform-console/nav';
import { FeatureFlagDetailPage } from 'components/platform-console/sections/feature-flags/detail';
import { FeatureFlagsSection } from 'components/platform-console/sections/feature-flags';
import { JobsSection } from 'components/platform-console/sections/jobs';
import { useIsPlatformConsoleUser } from 'hooks/useIsPlatformConsoleUser';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { PlatformConsoleService } from 'services/platform-console.service';

type AccessState = 'checking' | 'granted' | 'denied';

const RedirectToDefault = () => (
  <Navigate to={paths.platformConsole.default} replace />
);

const PlatformConsoleHeader = () => {
  const navigate = useNavigate();

  return (
    <Box
      style={{
        borderBottom: '1px solid var(--gray-a5)',
        backgroundColor: 'var(--color-panel-solid)',
      }}
    >
      <Container size="4" px="4">
        <Flex align="center" justify="between" py="3">
          <Flex align="center" gap="3">
            <Flex
              align="center"
              justify="center"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-3)',
                backgroundColor: 'var(--indigo-a3)',
                color: 'var(--indigo-11)',
              }}
            >
              <RocketIcon width="20" height="20" />
            </Flex>

            <Flex direction="column" gap="1">
              <Flex align="center" gap="2">
                <Heading size="5" trim="both">
                  Platform Console
                </Heading>
                <Badge color="indigo" variant="soft" radius="full">
                  Trajekt Staff
                </Badge>
              </Flex>
              <Text size="1" color="gray">
                Internal tooling for Trajekt staff
              </Text>
            </Flex>
          </Flex>

          <Button
            size="2"
            variant="soft"
            color="gray"
            onClick={() => navigate(paths.home)}
          >
            <ArrowLeftIcon />
            Return to app
          </Button>
        </Flex>
      </Container>
    </Box>
  );
};

/**
 * Standalone, sidebar-less layout for internal Trajekt staff tooling. Mounted
 * outside the main app chrome (see components/main). Access is guarded both by
 * the client-side predicate and a server-authoritative access-check.
 */
export const PlatformConsoleLayout = () => {
  const isPlatformConsoleUser = useIsPlatformConsoleUser();

  const [access, setAccess] = useState<AccessState>('checking');

  useEffect(() => {
    if (!isPlatformConsoleUser) {
      setAccess('denied');
      return;
    }

    let active = true;

    PlatformConsoleService.getInstance()
      .checkAccess()
      .then((granted) => {
        if (active) {
          setAccess(granted ? 'granted' : 'denied');
        }
      });

    return () => {
      active = false;
    };
  }, [isPlatformConsoleUser]);

  if (access === 'denied') {
    return <Navigate to={paths.home} replace />;
  }

  if (access === 'checking') {
    return null;
  }

  return (
    <ErrorBoundary identifier="PlatformConsole">
      {/*
       * The console renders its own standalone layout (Main returns early before
       * AuthorizedMain), so it must mount its own toast listener — otherwise
       * NotifyHelper.* events fire with nothing to render them.
       */}
      <NotificationListener />

      <Flex
        direction="column"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          backgroundColor: 'var(--gray-2)',
        }}
      >
        <PlatformConsoleHeader />

        <Box flexGrow="1" style={{ overflowY: 'auto' }}>
          <Container size="4" px="4" py="5">
            <Flex direction="column" gap="4">
              <PlatformConsoleNav />

              <Card size="3">
                <Routes>
                  {/*
                   * The console is rendered directly by Main (router path "*"),
                   * so these descendant routes match the full pathname and must
                   * use absolute paths (like SectionRouter), not the relative
                   * routePaths that only work under a "/section/*" wrapper.
                   */}
                  <Route
                    path={paths.platformConsole.root}
                    element={<RedirectToDefault />}
                  />
                  <Route
                    path={paths.platformConsole.featureFlags}
                    element={<FeatureFlagsSection />}
                  />
                  <Route
                    path={paths.platformConsole.featureFlagCreate}
                    element={<FeatureFlagDetailPage mode="create" />}
                  />
                  <Route
                    path={paths.platformConsole.featureFlagDetailPattern}
                    element={<FeatureFlagDetailPage mode="edit" />}
                  />
                  <Route
                    path={paths.platformConsole.jobs}
                    element={<JobsSection />}
                  />
                  <Route path="*" element={<RedirectToDefault />} />
                </Routes>
              </Card>
            </Flex>
          </Container>
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
